import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefCallback } from 'react';
import { lancerMontage } from './montage.js';

/**
 * MONTER UN MOTEUR QUI N'EST PAS UN COMPOSANT REACT (archetype B : une carte
 * mentale, un tableur, un editeur qui prend un element du DOM et s'y installe).
 *
 * Quatre pieges, tenus ici une fois pour toutes :
 *  - LE DOUBLE MONTAGE DE STRICTMODE. En dev, React monte, demonte puis
 *    remonte chaque effet : un moteur monte « une fois » dans un effet sans
 *    demontage symetrique laisse deux instances dans le meme element (deux
 *    barres d'outils, des ecouteurs en double). Ici, chaque montage a son
 *    demontage, dans le meme effet ;
 *  - LE MOTEUR ANNULE QUI EFFACE LE SUIVANT. Le montage annule par StrictMode
 *    est detruit APRES que le suivant s'est installe, et un moteur vide son
 *    element a la destruction : chaque montage recoit donc son propre HOTE, un
 *    enfant neuf de l'element (`montage.ts`, constate avec mind-elixir) ;
 *  - LE MONTAGE ASYNCHRONE. Un moteur charge paresseusement (`await import`)
 *    peut finir de monter APRES que l'ecran a ete quitte : il est demonte des
 *    qu'il arrive, au lieu de vivre dans un element detache ;
 *  - L'ELEMENT. On passe par une ref-callback : l'effet sait quand l'element
 *    existe vraiment, sans `useLayoutEffect` sur une ref qui peut etre nulle.
 *
 * `cle` REMONTE le moteur quand elle change (on ouvre un autre contenu) : un
 * moteur vanilla ne sait presque jamais changer de document en place.
 */
export interface OptionsDeMoteurVanilla<M> {
  /**
   * Installe le moteur dans l'element ; peut etre asynchrone. L'element recu
   * est l'HOTE de ce montage (une `div` qui remplit l'element de la ref), pas
   * l'element de la ref lui-meme.
   */
  monter(element: HTMLDivElement): M | Promise<M>;
  /** Le retire (destroy, dispose) ; appele une fois par montage. */
  demonter(moteur: M): void;
  /** Change : on demonte et on remonte. */
  cle?: string;
}

export interface MoteurVanilla<M> {
  /** A poser sur l'element qui accueille le moteur (`<div ref={ref} />`). */
  ref: RefCallback<HTMLDivElement>;
  /** Le moteur monte, ou `null` (pas encore, ou plus). */
  moteur: M | null;
  /** Le montage a leve : le message, pour l'etat d'erreur du cadre. */
  erreur: string | null;
}

export function useMoteurVanilla<M>(options: OptionsDeMoteurVanilla<M>): MoteurVanilla<M> {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [moteur, setMoteur] = useState<M | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  // Les fonctions de l'app changent d'identite a chaque rendu : on garde les
  // dernieres dans une ref, pour que seuls l'element et la cle remontent.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const ref = useCallback<RefCallback<HTMLDivElement>>((noeud) => setElement(noeud), []);

  useEffect(() => {
    if (element === null) return;
    setErreur(null);
    const demonter = lancerMontage<M, HTMLDivElement>({
      creerHote: () => {
        const hote = element.ownerDocument.createElement('div');
        hote.style.width = '100%';
        hote.style.height = '100%';
        hote.dataset.moteurHote = '';
        element.appendChild(hote);
        return hote;
      },
      monter: (hote) => optionsRef.current.monter(hote),
      demonter: (instance) => optionsRef.current.demonter(instance),
      siMonte: (instance) => setMoteur(() => instance),
      siErreur: (cause) => {
        console.error('[moteur] Le moteur n a pas pu etre monte :', cause);
        setErreur(cause instanceof Error ? cause.message : String(cause));
      },
    });
    return () => {
      setMoteur(null);
      demonter();
    };
  }, [element, options.cle]);

  return { ref, moteur, erreur };
}
